export const NOTIFICATION_DOMAINS = ["item", "platform"] as const;

export type NotificationDomain = (typeof NOTIFICATION_DOMAINS)[number];

export const PLATFORM_CHANGE_TYPES = [
  "new_feature",
  "feature_improvement",
  "bug_fix",
  "performance",
  "security",
  "policy_or_terms",
  "announcement",
] as const;

export type PlatformChangeType = (typeof PLATFORM_CHANGE_TYPES)[number];

export const PLATFORM_UPDATE_AUDIENCES = ["admin", "user", "both"] as const;
export type PlatformUpdateAudience = (typeof PLATFORM_UPDATE_AUDIENCES)[number];

export const PLATFORM_UPDATE_STATUSES = [
  "draft",
  "ready_for_review",
  "approved",
  "live",
  "published",
  "archived",
] as const;
export type PlatformUpdateStatus = (typeof PLATFORM_UPDATE_STATUSES)[number];

export const PLATFORM_UPDATE_PRIORITIES = ["low", "normal", "high"] as const;
export type PlatformUpdatePriority =
  (typeof PLATFORM_UPDATE_PRIORITIES)[number];

const CHANGE_TYPE_LABELS: Record<PlatformChangeType, string> = {
  new_feature: "New Feature",
  feature_improvement: "Feature Improvement",
  bug_fix: "Bug Fix",
  performance: "Performance",
  security: "Security",
  policy_or_terms: "Policy or Terms",
  announcement: "Announcement",
};

const STATUS_TRANSITIONS: Record<
  PlatformUpdateStatus,
  readonly PlatformUpdateStatus[]
> = {
  draft: ["ready_for_review", "archived"],
  ready_for_review: ["draft", "approved", "archived"],
  approved: ["ready_for_review", "live", "archived"],
  live: ["approved", "archived"],
  published: ["live", "archived"],
  archived: ["draft"],
};

export function isNotificationDomain(
  value: string,
): value is NotificationDomain {
  return (NOTIFICATION_DOMAINS as readonly string[]).includes(value);
}

export function isPlatformChangeType(
  value: string,
): value is PlatformChangeType {
  return (PLATFORM_CHANGE_TYPES as readonly string[]).includes(value);
}

export function isPlatformUpdateAudience(
  value: string,
): value is PlatformUpdateAudience {
  return (PLATFORM_UPDATE_AUDIENCES as readonly string[]).includes(value);
}

export function isPlatformUpdateStatus(
  value: string,
): value is PlatformUpdateStatus {
  return (PLATFORM_UPDATE_STATUSES as readonly string[]).includes(value);
}

export function isPlatformUpdatePriority(
  value: string,
): value is PlatformUpdatePriority {
  return (PLATFORM_UPDATE_PRIORITIES as readonly string[]).includes(value);
}

export function canTransitionPlatformUpdateStatus(
  from: PlatformUpdateStatus,
  to: PlatformUpdateStatus,
): boolean {
  if (from === to) return false;
  return STATUS_TRANSITIONS[from].includes(to);
}

export function platformUpdateVisibleToUsers(input: {
  status: PlatformUpdateStatus;
  publicVisibility: boolean;
  internalAudience: PlatformUpdateAudience;
  isLive: boolean;
}): boolean {
  return (
    input.status === "published" &&
    input.publicVisibility &&
    input.isLive &&
    (input.internalAudience === "user" || input.internalAudience === "both")
  );
}

export function getPlatformChangeTypeLabel(type: PlatformChangeType): string {
  return CHANGE_TYPE_LABELS[type];
}
